import React, { useState } from 'react';
import { Network, Settings2, Play, Target, Sparkles, CheckCircle2 } from 'lucide-react';
import { useAppContext } from '../contexts/AppContext';

const AdvancedConvergencePage: React.FC = () => {
    const { theme } = useAppContext();
    const isDark = theme === 'dark';

    // Convergence Options State
    const [options, setOptions] = useState({
        lowestRpn: false,
        lowestCost: false,
        leastComponents: false,
        reuseParts: false,
        standardParts: false
    });

    const handleOptionChange = (option: keyof typeof options) => {
        setOptions(prev => ({
            ...prev,
            [option]: !prev[option]
        }));
    };

    return (
        <div className={`p-6 max-w-6xl mx-auto space-y-6 ${isDark ? 'text-gray-100' : 'text-gray-800'}`}>
            <div className={`p-6 rounded-2xl shadow-sm border ${isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-blue-100'}`}>
                <div className="flex items-center gap-3 mb-2">
                    <div className="w-10 h-10 rounded-lg bg-blue-100 dark:bg-blue-900/40 flex items-center justify-center">
                        <Network className="text-blue-600 dark:text-blue-400" size={24} />
                    </div>
                    <div>
                        <h1 className="text-2xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-blue-600 to-indigo-600 dark:from-blue-400 dark:to-indigo-400">
                            Advanced Convergence Playground
                        </h1>
                        <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                            Multi-Epoch Optimization using Fiber Bundle & Wavelet Analysis
                        </p>
                    </div>
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Configuration Panel */}
                <div className="lg:col-span-1 space-y-6">
                    <div className={`p-6 rounded-2xl shadow-sm border ${isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-gray-100'}`}>
                        <div className="flex items-center gap-2 mb-4 pb-4 border-b border-gray-200 dark:border-slate-700">
                            <Settings2 className="text-indigo-500" size={20} />
                            <h2 className="text-lg font-semibold">Convergence Targets</h2>
                        </div>
                        
                        <div className="space-y-4">
                            <label className="flex items-center gap-3 p-3 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-700/50 cursor-pointer transition-colors border border-transparent hover:border-slate-200 dark:hover:border-slate-600">
                                <input 
                                    type="checkbox" 
                                    className="w-5 h-5 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                    checked={options.lowestRpn}
                                    onChange={() => handleOptionChange('lowestRpn')}
                                />
                                <span className={options.lowestRpn ? 'font-medium' : ''}>Lowest RPN (Risk Priority)</span>
                            </label>
                            
                            <label className="flex items-center gap-3 p-3 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-700/50 cursor-pointer transition-colors border border-transparent hover:border-slate-200 dark:hover:border-slate-600">
                                <input 
                                    type="checkbox" 
                                    className="w-5 h-5 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                    checked={options.lowestCost}
                                    onChange={() => handleOptionChange('lowestCost')}
                                />
                                <span className={options.lowestCost ? 'font-medium' : ''}>Lowest Total Cost</span>
                            </label>
                            
                            <label className="flex items-center gap-3 p-3 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-700/50 cursor-pointer transition-colors border border-transparent hover:border-slate-200 dark:hover:border-slate-600">
                                <input 
                                    type="checkbox" 
                                    className="w-5 h-5 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                    checked={options.leastComponents}
                                    onChange={() => handleOptionChange('leastComponents')}
                                />
                                <span className={options.leastComponents ? 'font-medium' : ''}>Least Number of Components</span>
                            </label>

                            <label className="flex items-center gap-3 p-3 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-700/50 cursor-pointer transition-colors border border-transparent hover:border-slate-200 dark:hover:border-slate-600">
                                <input 
                                    type="checkbox" 
                                    className="w-5 h-5 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                    checked={options.reuseParts}
                                    onChange={() => handleOptionChange('reuseParts')}
                                />
                                <span className={options.reuseParts ? 'font-medium' : ''}>Maximize Reuse of Existing Parts</span>
                            </label>

                            <label className="flex items-center gap-3 p-3 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-700/50 cursor-pointer transition-colors border border-transparent hover:border-slate-200 dark:hover:border-slate-600">
                                <input 
                                    type="checkbox" 
                                    className="w-5 h-5 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                    checked={options.standardParts}
                                    onChange={() => handleOptionChange('standardParts')}
                                />
                                <span className={options.standardParts ? 'font-medium' : ''}>Prefer Standard/Off-The-Shelf Parts</span>
                            </label>
                        </div>
                        
                        <div className="mt-8">
                            <button className="w-full flex items-center justify-center gap-2 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white py-3 px-4 rounded-xl font-medium transition-all shadow-md shadow-blue-500/20 hover:shadow-lg hover:shadow-blue-500/30">
                                <Play size={18} fill="currentColor" />
                                Run Target Convergence
                            </button>
                        </div>
                    </div>
                </div>

                {/* Visualization / Output Panel */}
                <div className="lg:col-span-2 space-y-6">
                    <div className={`p-6 rounded-2xl shadow-sm border h-full min-h-[400px] flex flex-col items-center justify-center text-center ${isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-gray-100'}`}>
                        <div className="w-20 h-20 rounded-full bg-slate-100 dark:bg-slate-700 flex items-center justify-center mb-4">
                            <Sparkles className="text-slate-400 dark:text-slate-500" size={32} />
                        </div>
                        <h3 className="text-xl font-semibold mb-2">Ready for Convergence</h3>
                        <p className={`max-w-md ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                            Select your targets on the left and run the convergence kernel to find the optimal global design trajectory across epochs.
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AdvancedConvergencePage;
